import { TextField } from '@material-ui/core';
import { ChangeEvent, useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';

interface LambdaNameInputProps {
  currentName: string;
  onRename: (oldName: string, newName: string) => void;
}

export interface LambdaNameInputRef {
  focus: () => void;
}

export const LambdaNameInput = forwardRef<LambdaNameInputRef, LambdaNameInputProps>(
  ({ currentName, onRename }, ref) => {
    const [localName, setLocalName] = useState(currentName);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        // Use requestAnimationFrame to ensure the input is fully mounted and visible
        // before focusing (e.g., when rendered inside dialogs or animated containers)
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      },
    }));

    useEffect(() => {
      setLocalName(currentName);
    }, [currentName]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const formatted = value
        .replaceAll(/\s+/g, '_')
        .replaceAll(/[^a-zA-Z0-9\s_]/g, '')
        .toLowerCase();
      setLocalName(formatted);
    };

    const handleBlur = () => {
      if (localName !== currentName && localName.trim()) {
        onRename(currentName, localName.trim());
      } else if (!localName.trim()) {
        setLocalName(currentName);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleBlur();
      }
    };

    return (
      <TextField
        inputRef={inputRef}
        value={localName}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        variant="outlined"
        size="small"
        fullWidth
        label="Lambda Name"
        placeholder="e.g., example_handler"
      />
    );
  },
);

LambdaNameInput.displayName = 'LambdaNameInput';
